// =============================================================================
// src/components/AgentSyncBar.jsx  (SOCKET-DRIVEN — no more 35s timeout)
// =============================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMyAgents } from '../hooks/useMyAgents';
import { useSyncStatus } from '../hooks/useSyncStatus';
import '../styles/AgentSyncBar.css';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000';
const TALLY_CHECK_INTERVAL_MS = 10_000;
const SYNC_SAFETY_TIMEOUT_MS  = 5 * 60 * 1000;

function getAuthHeaders() {
  try {
    const saved = localStorage.getItem('auditpros_user');
    if (!saved) return {};
    const parsed = JSON.parse(saved);
    const account = parsed?.account;
    const email = account?.idTokenClaims?.email
      || account?.idTokenClaims?.preferred_username
      || account?.idTokenClaims?.emails?.[0]
      || account?.username || account?.email;
    const h = { 'Content-Type': 'application/json' };
    if (email) h['x-user-email'] = email;
    return h;
  } catch { return {}; }
}

export default function AgentSyncBar({ onSyncStarted }) {
  const { primaryAgent, loading, error, refresh } = useMyAgents();
  const { isSyncing, stage, lastErrorAt, lastErrorPayload, markStarted, markIdle } = useSyncStatus();

  const [tallyCheck, setTallyCheck] = useState(null);
  const [checking, setChecking]     = useState(false);
  const [message, setMessage]       = useState(null);
  const [localSyncing, setLocalSyncing] = useState(false);

  const safetyTimerRef = useRef(null);
  const tallyPollRef   = useRef(null);
  const seenErrorAtRef = useRef(lastErrorAt);

  const checkTally = useCallback(async () => {
    if (!primaryAgent?.id) return;
    setChecking(true);
    try {
      const res = await fetch(
        `${BASE}/agents/${primaryAgent.id}/check-tally`,
        { headers: getAuthHeaders() }
      );
      if (!res.ok) { setTallyCheck(null); return; }
      const data = await res.json();
      setTallyCheck(data);
    } catch {
      setTallyCheck(null);
    } finally {
      setChecking(false);
    }
  }, [primaryAgent?.id]);

  useEffect(() => {
    if (!isSyncing && localSyncing) {
      setLocalSyncing(false);
      if (safetyTimerRef.current) {
        clearTimeout(safetyTimerRef.current);
        safetyTimerRef.current = null;
      }
      checkTally();

      const failed = lastErrorAt && lastErrorAt !== seenErrorAtRef.current;
      seenErrorAtRef.current = lastErrorAt;

      if (failed) {
        setMessage({
          type: 'error',
          text: lastErrorPayload?.error ? `✕ Sync failed: ${lastErrorPayload.error}` : '✕ Sync failed. Please try again.',
        });
        setTimeout(() => setMessage(null), 8000);
      } else {
        setMessage({ type: 'success', text: '✓ Sync complete' });
        setTimeout(() => setMessage(null), 4000);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSyncing, lastErrorAt, lastErrorPayload]);

  const syncing = isSyncing || localSyncing;

  useEffect(() => {
    if (!primaryAgent?.id) { setTallyCheck(null); return; }
    checkTally();
    tallyPollRef.current = setInterval(checkTally, TALLY_CHECK_INTERVAL_MS);
    const onFocus = () => checkTally();
    window.addEventListener('focus', onFocus);
    return () => {
      if (tallyPollRef.current) clearInterval(tallyPollRef.current);
      tallyPollRef.current = null;
      window.removeEventListener('focus', onFocus);
    };
  }, [primaryAgent?.id, checkTally]);

  useEffect(() => {
    return () => { if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current); };
  }, []);

  const handleSync = useCallback(async () => {
    if (!primaryAgent?.id) return;

    setLocalSyncing(true);
    markStarted();
    setMessage(null);

    safetyTimerRef.current = setTimeout(() => {
      setLocalSyncing(false);
      setMessage({ type: 'error', text: 'Sync is taking longer than expected. Check the table below.' });
      setTimeout(() => setMessage(null), 6000);
    }, SYNC_SAFETY_TIMEOUT_MS);

    try {
      const res = await fetch(
        `${BASE}/agents/${primaryAgent.id}/request-sync`,
        { method: 'POST', headers: getAuthHeaders() }
      );

      if (res.status === 429) {
        setLocalSyncing(false);
        markIdle();
        if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
        setMessage({ type: 'error', text: 'Please wait a moment before syncing again.' });
        setTimeout(() => setMessage(null), 4000);
        return;
      }

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setLocalSyncing(false);
        markIdle();
        if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
        setMessage({ type: 'error', text: data.message || `Sync could not start: ${res.status}` });
        setTimeout(() => setMessage(null), 6000);
        return;
      }

      if (onSyncStarted) {
        onSyncStarted({ requestId: data.requestId, agentId: primaryAgent.id });
      }
    } catch {
      setLocalSyncing(false);
      markIdle();
      if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
      setMessage({ type: 'error', text: 'Sync request failed. Check your connection and try again.' });
      setTimeout(() => setMessage(null), 6000);
    }
  }, [primaryAgent, onSyncStarted, markStarted]);

  // ── Render: Loading / error / no agent ───────────────────────────────────
  if (loading) {
    return (
      <div className="asb-bar">
        <span className="asb-muted">Loading agent status...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="asb-bar asb-bar--error">
        <span>Could not fetch agent status</span>
        <button onClick={refresh} className="asb-retry-btn">Retry</button>
      </div>
    );
  }

  if (!primaryAgent) {
    return (
      <div className="asb-bar asb-bar--no-agent">
        <span>
          ○ No agent installed.
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('audit-pros-navigate', { detail: 'devices' }))}
            className="asb-link-btn"
          >
            Install AuditPrOS Tally Connector on your PC
          </button>
        </span>
      </div>
    );
  }

  // ── Determine state ──────────────────────────────────────────────────────
  const agentOnlineFromCheck = tallyCheck ? tallyCheck.agentOnline : null;
  const agentOnline = agentOnlineFromCheck !== null ? agentOnlineFromCheck : primaryAgent.is_online;

  const tallyReachable   = tallyCheck?.reachable === true;
  const tallyUnreachable = tallyCheck?.agentOnline && tallyCheck.reachable === false;
  const canSync = agentOnline && tallyReachable && !syncing;

  let barVariant  = 'checking';
  let indicator   = '○';
  let statusLabel = `${primaryAgent.device_name} — Checking Tally...`;

  // A sync in progress can legitimately keep the agent's event loop busy
  // (parsing/normalising a large Tally export) long enough to miss the
  // 3s check-tally ack — that's the agent being BUSY, not offline. Trust
  // that the sync we ourselves started is still running rather than
  // flashing a false "Agent is offline" over an active sync.
  if (syncing) {
    barVariant  = 'online';
    indicator   = '●';
    statusLabel = `${primaryAgent.device_name} — Sync in progress...`;
  } else if (!agentOnline) {
    barVariant  = 'offline';
    indicator   = '○';
    statusLabel = `${primaryAgent.device_name} — Agent is offline`;
  } else if (tallyReachable) {
    barVariant  = 'online';
    indicator   = '●';
    statusLabel = `${primaryAgent.device_name} — Tally: ${tallyCheck?.activeCompany || 'Ready'}`;
  } else if (tallyUnreachable) {
    barVariant  = 'tally-down';
    indicator   = '○';
    statusLabel = `${primaryAgent.device_name} — Tally is not running. Please open Tally.`;
  }

  const syncLabel = syncing
    ? (stage ? `⟳ ${stage}...` : '⟳ Syncing...')
    : '↻ Sync from Tally';

  const msgVariant = message?.type === 'error' ? 'error'
                   : message?.type === 'success' ? 'success'
                   : 'info';

  return (
    <div className={`asb-bar asb-bar--${barVariant}`}>
      <div className="asb-status-wrap">
        <span className="asb-status-text">{indicator} {statusLabel}</span>
        {checking && tallyCheck !== null && (
          <span className="asb-checking">(checking...)</span>
        )}
      </div>

      <button
        onClick={handleSync}
        disabled={!canSync}
        className="asb-sync-btn"
        title={
          syncing           ? 'Syncing...'
          : !agentOnline    ? 'Agent is offline'
          : tallyUnreachable ? 'Tally is not running'
          : 'Sync from Tally'
        }
      >
        {syncLabel}
      </button>

      {message && (
        <div className={`asb-message asb-message--${msgVariant}`}>
          {message.text}
          <button onClick={() => setMessage(null)} className="asb-dismiss-btn">✕</button>
        </div>
      )}
    </div>
  );
}
